import { useState } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ReadOnlyTextProperty } from "@/components/doc/right-sidebar/node-viewer/NodeProperty";
import { FunctionNodeParameter } from "@/components/doc/right-sidebar/node-viewer/properties/FunctionNodeParameter";
import { isNodeEditable } from "@/components/tiptap/lock/utils";
import { twMerge } from "@/utils/twMerge";

import AddIcon from "./AddIcon.svg";

import type { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";

type Props = {
  node: FunctionNodeModel;
};

function FunctionNodeParametersField({ node, node: { variables } }: Props) {
  const t = useTranslations("sidebar");
  const hasParams = variables.length > 0;
  const [isVisible, setIsVisible] = useState(() => {
    return !hasParams;
  });

  return (
    <div className="group">
      {variables.map((n, i) => (
        <div key={n.id} className="relative flex items-center">
          <FunctionNodeParameter
            parent={node}
            node={n}
            label={i > 0 ? t("nodeProps.functionParams.and") : t("nodeProps.functionParams.of")}
          />
          {i === variables.length - 1 && (
            <button
              type="button"
              // `opacity`, not `visibility`: an invisible button can't take focus
              className={twMerge(
                "mr-2 opacity-0",
                !isVisible && "group-hover:opacity-100 focus-visible:opacity-100",
              )}
              disabled={isVisible}
              onClick={() => setIsVisible(true)}
              aria-label={t("nodeProps.functionParams.add")}
            >
              <AddIcon />
            </button>
          )}
        </div>
      ))}
      {isVisible && (
        <FunctionNodeParameter
          parent={node}
          autoFocus={hasParams}
          label={hasParams ? t("nodeProps.functionParams.and") : t("nodeProps.functionParams.of")}
          onSelect={() => setIsVisible(false)}
        />
      )}
    </div>
  );
}

const FunctionNodeParametersReadOnly = observer(function FunctionNodeParametersReadOnly({
  node,
}: Props) {
  const t = useTranslations("sidebar");
  const { variables } = node;

  if (variables.length === 0) {
    return null;
  }

  return (
    <div>
      {variables.map((variable, i) => (
        <ReadOnlyTextProperty
          key={variable.id}
          name={i > 0 ? t("nodeProps.functionParams.and") : t("nodeProps.functionParams.of")}
          value={variable.value ?? ""}
          mono
        />
      ))}
    </div>
  );
});

export const FunctionNodeParameters = observer(function FunctionNodeParameters(props: Props) {
  if (!isNodeEditable(props.node)) {
    return <FunctionNodeParametersReadOnly {...props} />;
  }

  return <FunctionNodeParametersField {...props} />;
});
